/**
 * Ref-counted scroll suppression while web dropdown/menu overlays are open.
 *
 * - `body` / `documentElement` overflow hidden catches any window-level scrolling.
 * - Capture-phase `wheel` + `touchmove` listeners (non-passive) catch nested scroll
 *   containers (e.g. React Native Web `ScrollView` in CreateRequest / RequestDetail).
 *
 * Events whose target is inside `[data-dropdown-portal="true"]` are not blocked so the
 * menu list and search field keep scrolling normally.
 */

let lockCount = 0;
let savedBodyOverflow = '';
let savedHtmlOverflow = '';

function isInsideDropdownPortal(target: EventTarget | null): boolean {
  if (typeof document === 'undefined' || !(target instanceof Node)) {
    return false;
  }
  const portals = document.querySelectorAll('[data-dropdown-portal="true"]');
  for (let i = 0; i < portals.length; i++) {
    if (portals[i].contains(target)) {
      return true;
    }
  }
  return false;
}

/** Overlays (modals) that scroll internally — must not be blocked by the global wheel listener. */
function isInsideScrollExemptOverlay(target: EventTarget | null): boolean {
  if (typeof document === 'undefined' || !(target instanceof Node)) {
    return false;
  }
  const overlays = document.querySelectorAll('[data-overlay-scroll-exempt="true"]');
  for (let i = 0; i < overlays.length; i++) {
    if (overlays[i].contains(target)) {
      return true;
    }
  }
  return false;
}

function preventBackgroundScroll(e: WheelEvent | TouchEvent): void {
  if (lockCount === 0) {
    return;
  }
  if (isInsideDropdownPortal(e.target) || isInsideScrollExemptOverlay(e.target)) {
    return;
  }
  e.preventDefault();
}

const captureOpts: AddEventListenerOptions = { capture: true, passive: false };

let listenersAttached = false;

function attachGlobalScrollBlockers(): void {
  if (listenersAttached || typeof window === 'undefined') {
    return;
  }
  listenersAttached = true;
  window.addEventListener('wheel', preventBackgroundScroll, captureOpts);
  window.addEventListener('touchmove', preventBackgroundScroll, captureOpts);
}

function detachGlobalScrollBlockers(): void {
  if (!listenersAttached || typeof window === 'undefined') {
    return;
  }
  listenersAttached = false;
  window.removeEventListener('wheel', preventBackgroundScroll, captureOpts);
  window.removeEventListener('touchmove', preventBackgroundScroll, captureOpts);
}

/**
 * Call when a web overlay menu opens. Uses reference counting for nested / multiple menus.
 */
export function acquireWebOverlayScrollLock(): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (lockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    savedHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    attachGlobalScrollBlockers();
  }
  lockCount += 1;
}

/**
 * Call when a web overlay menu closes; pairs with {@link acquireWebOverlayScrollLock}.
 */
export function releaseWebOverlayScrollLock(): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (lockCount <= 0) {
    return;
  }
  lockCount -= 1;
  if (lockCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
    document.documentElement.style.overflow = savedHtmlOverflow;
    savedBodyOverflow = '';
    savedHtmlOverflow = '';
    detachGlobalScrollBlockers();
  }
}
